/**
 * Demo mode: `VITE_VELORA_DEMO=1 pnpm --filter @velora/web dev` serves the
 * shell's own API routes and the identity session from a deterministic
 * synthetic practice, so the shell can be reviewed without a database, a
 * login or the seed. The fixture module is loaded lazily, so normal builds
 * never contain it. Nothing here is real patient data.
 */
export function isDemoMode(): boolean {
  return import.meta.env['VITE_VELORA_DEMO'] === '1';
}

export const demoFetch: typeof fetch = async (input, init) => {
  const { handleDemoRequest, setDemoRole, setDemoSkin } = await import('./fixtures.js');
  // VITE_VELORA_DEMO_ROLE=owner: every permission and add-on (admin screens).
  if (import.meta.env['VITE_VELORA_DEMO_ROLE'] === 'owner') setDemoRole('owner');
  const skin = import.meta.env['VITE_VELORA_DEMO_SKIN'];
  if (typeof skin === 'string' && skin) setDemoSkin(skin);
  // VITE_VELORA_DEMO_ROLE=analyst: Analytics without analytics.patient_map (map areas only).
  if (import.meta.env['VITE_VELORA_DEMO_ROLE'] === 'analyst') setDemoRole('analyst');
  const url = new URL(
    typeof input === 'string' ? input : input instanceof URL ? input.href : input.url,
    window.location.origin,
  );
  const headers = new Headers(init?.headers);
  const res = handleDemoRequest({
    method: init?.method ?? 'GET',
    path: url.pathname,
    search: url.searchParams,
    office: headers.get('x-velora-office'),
    body: typeof init?.body === 'string' ? (JSON.parse(init.body) as unknown) : undefined,
  });
  return new Response(res.body === undefined ? null : JSON.stringify(res.body), {
    status: res.status,
    headers: { 'content-type': 'application/json' },
  });
};

/**
 * The identity module's client resolves `globalThis.fetch` per call. In demo
 * mode, route its session requests (`/v1/identity/...`) to the demo practice
 * so the sign-in gate opens onto the shell. A no-op outside demo mode.
 */
export function installDemoIdentityFetch(): void {
  if (!isDemoMode() || typeof window === 'undefined') return;
  const real = window.fetch.bind(window);
  window.fetch = (input, init) => {
    const href = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const { pathname } = new URL(href, window.location.origin);
    return pathname.startsWith('/v1/identity/') ? demoFetch(input, init) : real(input, init);
  };
}
